document.addEventListener('DOMContentLoaded', function () {
  'use strict';
  const wrap = document.querySelector('[data-cart-recommendations]');
  if (!wrap) return;
  const grid = wrap.querySelector('.recently-viewed');
  const title = wrap.querySelector('#render-recommendations-title');
  const configNode = document.querySelector('[data-dac-empty-cart-config]');
  if (!grid || !title || !configNode) return;
  let config;
  try { config = JSON.parse(configNode.textContent); } catch (_) { return; }
  const authenticated = wrap.dataset.authenticated === 'true';
  const ownership = window.DACCartOwnership;
  if (authenticated && (config.authenticated !== true || !ownership || !Array.isArray(config.ownedSKU))) return;

  let interacted = false;
  let shownPriority = -1;
  let history = null;
  const jobs = new Set();
  const pending = [];
  const requests = new Map();
  let activeReads = 0;
  function preserveInteraction() {
    interacted = true;
    jobs.forEach(job => { job.controller.abort(); job.finish(null); });
  }
  wrap.addEventListener('pointerdown', preserveInteraction, { once: true });
  wrap.addEventListener('focusin', preserveInteraction, { once: true });
  window.addEventListener('pagehide', preserveInteraction, { once: true });

  const historyReady = authenticated
    ? Promise.resolve().then(() => ownership.load(config)).then(value => {
      if (!value || value.authenticated !== true || value.complete !== true) return false;
      history = value;
      return true;
    }).catch(() => false)
    : Promise.resolve(true);

  function productMetadata(id, skus, variantsCount) {
    const count = Number(variantsCount);
    if (!id || !Array.isArray(skus) || !Number.isInteger(count) || count < 1 || count > 20 || count !== skus.length) return null;
    if (skus.some(sku => typeof sku !== 'string' && sku !== null)) return null;
    return { id: String(id), variantsCount: count, variants: skus.map(sku => ({ sku: sku || '' })) };
  }
  function excluded(product) {
    if (!product) return true;
    if (!authenticated) return false;
    if (!history || history.complete !== true) return true;
    try { return ownership.excludes(product, history, config.ownedSKU); } catch (_) { return true; }
  }
  function candidateURL(value) {
    try {
      const parsed = new URL(value, location.origin);
      if (!/^https?:$/.test(parsed.protocol)) return null;
      const match = parsed.pathname.match(/\/products\/([^/]+)\/?$/);
      if (!match) return null;
      const handle = decodeURIComponent(match[1]);
      if (!handle || /[\/\\?#]/.test(handle)) return null;
      const base = new URL(wrap.dataset.productsRoot || window.Shopify?.routes?.root || '/', location.origin);
      if (base.origin !== location.origin) return null;
      base.pathname = base.pathname.replace(/\/+$/, '') + '/products/' + encodeURIComponent(handle);
      base.search = '';
      base.searchParams.set('section_id', wrap.dataset.productCardSection);
      const preview = new URL(location.href).searchParams;
      ['preview_theme_id', '_fd', 'pb'].forEach(key => { if (preview.has(key)) base.searchParams.set(key, preview.get(key)); });
      const activeTheme = window.Shopify?.theme;
      if (activeTheme && String(activeTheme.id) !== String(config.theme)) return null;
      if (activeTheme?.role === 'unpublished') {
        if (!/^\d+$/.test(String(config.theme))) return null;
        base.searchParams.set('preview_theme_id', String(config.theme));
      } else if (activeTheme?.role === 'main') {
        ['preview_theme_id', '_fd', 'pb'].forEach(key => base.searchParams.delete(key));
      }
      return base;
    } catch (_) { return null; }
  }
  function drainRequests() {
    while (activeReads < 4 && pending.length) {
      const job = pending.shift();
      if (job.done || interacted || !wrap.isConnected) { job.finish(null); continue; }
      activeReads++;
      (async function () {
        try {
          const response = await fetch(job.url.href, { credentials: 'same-origin', cache: 'no-store', signal: job.controller.signal });
          if (!response.ok) return null;
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const card = doc.querySelector('article[data-dac-empty-cart-product-id]');
          if (!card) return null;
          let skus;
          try { skus = JSON.parse(card.dataset.dacEmptyCartSkus); } catch (_) { return null; }
          const product = productMetadata(card.dataset.dacEmptyCartProductId, skus, card.dataset.dacEmptyCartVariantsCount);
          if (excluded(product)) return null;
          return { id: product.id, html: card.outerHTML };
        } catch (_) { return null; }
      })().then(job.finish).finally(() => { activeReads--; drainRequests(); });
    }
  }
  function currentCard(url, deadline) {
    const remaining = deadline - Date.now();
    if (remaining <= 0) return Promise.resolve(null);
    if (requests.has(url.href)) return requests.get(url.href);
    const promise = new Promise(resolve => {
      const job = { url, controller: new AbortController(), done: false, timer: null };
      job.finish = result => {
        if (job.done) return;
        job.done = true;
        clearTimeout(job.timer);
        jobs.delete(job);
        resolve(result);
      };
      job.timer = setTimeout(() => { job.controller.abort(); job.finish(null); }, Math.min(10000, remaining));
      jobs.add(job);
      pending.push(job);
    });
    requests.set(url.href, promise);
    drainRequests();
    return promise;
  }
  async function beforeDeadline(promise, deadline) {
    const remaining = deadline - Date.now();
    if (remaining <= 0) return null;
    let timer;
    try {
      return await Promise.race([promise, new Promise(resolve => { timer = setTimeout(() => resolve(null), remaining); })]);
    } finally { clearTimeout(timer); }
  }
  async function renderCurrentProducts(values, heading, priority, limit) {
    if (!Array.isArray(values) || !await historyReady || interacted || !wrap.isConnected) return;
    const seen = new Set();
    const urls = [];
    for (const value of values) {
      if (urls.length >= limit) break;
      if (value && typeof value === 'object') {
        const metadata = productMetadata(value.id, value.skus, value.variantsCount);
        if (excluded(metadata)) continue;
      }
      const url = candidateURL(value && typeof value === 'object' ? value.url : value);
      if (!url || seen.has(url.pathname)) continue;
      seen.add(url.pathname);
      urls.push(url);
    }
    const cards = [];
    const products = new Set();
    const deadline = Date.now() + 10000;
    for (let offset = 0; offset < urls.length && cards.length < 4; offset += 4) {
      if (interacted || !wrap.isConnected || shownPriority > priority || Date.now() >= deadline) return;
      const batch = await beforeDeadline(Promise.all(urls.slice(offset, offset + 4).map(url => currentCard(url, deadline))), deadline);
      if (!batch) return;
      batch.forEach(card => {
        if (!card || cards.length >= 4 || products.has(card.id)) return;
        products.add(card.id);
        cards.push(card.html);
      });
    }
    if (!cards.length || interacted || !wrap.isConnected || shownPriority > priority || Date.now() >= deadline) return;
    grid.innerHTML = cards.join('');
    title.textContent = heading;
    wrap.hidden = false;
    shownPriority = priority;
    document.dispatchEvent(new Event('dac:cards-added'));
  }

  if (authenticated) renderCurrentProducts(config.fallback, title.textContent, 0, 100).catch(() => {});
  try {
    const saved = JSON.parse(localStorage.getItem('recentlyViewedProduct') || '[]');
    if (Array.isArray(saved)) {
      const urls = saved.slice(-20).reverse().map(item => item && item.productUrl);
      renderCurrentProducts(urls, wrap.dataset.recentTitle, 1, 20).catch(() => {});
    }
  } catch (_) {}
  let wishlistRequested = false;
  window.SwymCallbacks = window.SwymCallbacks || [];
  window.SwymCallbacks.push(function (swat) {
    if (wishlistRequested || interacted || !wrap.isConnected) return;
    wishlistRequested = true;
    try {
      swat.fetch(products => {
        if (Array.isArray(products)) renderCurrentProducts(products.map(product => product && product.du), wrap.dataset.wishlistTitle, 2, 20).catch(() => {});
      });
    } catch (_) {}
  });
});
